import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useCharacterSheet } from "@/hooks/use-character-sheet";
import type { PartyPc } from "@/lib/core/schemas/party-view";
import type {
  CharacterFeatureDnd5e,
  CharacterFeatureSource,
} from "@/lib/rules/dnd5e/character/character-sheet";

const FEATURE_GROUPS: ReadonlyArray<{
  source: CharacterFeatureSource;
  label: string;
}> = [
  { source: "class", label: "Class Features" },
  { source: "subclass", label: "Subclass Features" },
  { source: "species", label: "Species" },
  { source: "background", label: "Background" },
  { source: "feat", label: "Feats" },
  { source: "equipment", label: "Equipment" },
  { source: "other", label: "Other" },
];

function CompactState({ children }: { children: string }) {
  return (
    <div className="py-8 text-center text-muted-foreground text-sm">
      {children}
    </div>
  );
}

function FeatureRow({ feature }: { feature: CharacterFeatureDnd5e }) {
  const description = feature.description.trim();

  return (
    <li className="space-y-1.5">
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        <span className="min-w-0 font-medium text-foreground text-sm">
          {feature.label}
        </span>
        <Badge className="text-[10px]" variant="secondary">
          {feature.sourceLabel}
        </Badge>
        {feature.level === undefined ? null : (
          <Badge className="text-[10px]" variant="outline">
            Level {feature.level}
          </Badge>
        )}
      </div>
      {description === "" ? null : (
        <p className="text-muted-foreground text-sm">{description}</p>
      )}
    </li>
  );
}

export function FeaturesPanel({
  pc,
  campaignId,
}: {
  pc: PartyPc | null;
  campaignId: string;
}) {
  const { sheet, isLoading, error } = useCharacterSheet(
    campaignId,
    pc?.id ?? null
  );

  if (!pc) {
    return <CompactState>No character selected.</CompactState>;
  }

  if (error) {
    return <CompactState>Couldn't load character features.</CompactState>;
  }

  if (isLoading || !sheet) {
    return <CompactState>Loading...</CompactState>;
  }

  if (sheet.features.length === 0) {
    return <CompactState>No readable features are available yet.</CompactState>;
  }

  return (
    <div className="space-y-3 pb-16">
      {FEATURE_GROUPS.map((group) => {
        const features = sheet.features.filter(
          (feature) => feature.source === group.source
        );

        if (features.length === 0) {
          return null;
        }

        const titleId = `features-panel-${group.source}`;

        return (
          <section aria-labelledby={titleId} key={group.source}>
            <Card className="border-border bg-card">
              <CardHeader className="p-4 pb-2">
                <h3
                  className="font-semibold text-base text-foreground tracking-tight"
                  id={titleId}
                >
                  {group.label}
                </h3>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <ul className="space-y-3">
                  {features.map((feature) => (
                    <FeatureRow feature={feature} key={feature.id} />
                  ))}
                </ul>
              </CardContent>
            </Card>
          </section>
        );
      })}
    </div>
  );
}
